import { Component, OnInit, Optional } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { FeatureHelperService } from '../../_helpers/feature-helper/feature-helper.service';
import { ErrorModalHelperService } from '../../_helpers/error-modal-helper/error-modal-helper.service';
import { SuccessModalHelperService } from '../../_helpers/success-modal-helper/success-modal-helper.service';
import { DynamicConfirmModalComponent } from '../../shared/dynamic-confirm-modal/dynamic-confirm-modal.component';
import { ColumnDef, DEFAULT_PAGE_SIZE_OPTIONS, StatusVariant } from '../../shared/data-table/data-table.model';
import { readRememberedPageSize, rememberPageSize } from '../../shared/data-table/page-size-storage';
import { QaService } from '../../_services/qa/qa.service';
import { QaConsultant } from '../../_models/qa/qa-consultant.model';
import { QaReportRow, QaReportType, QaSessionLearner } from '../../_models/qa/qa-report-row.model';
import { LeapV2IlpService } from '../../tutor-reports/tutor-report-flows/tutor-report-leap-v2/services/leap-v2-ilp.service';
import { LeapV2ProgressReportService } from '../../tutor-reports/tutor-report-flows/tutor-report-leap-v2/services/leap-v2-progress-report.service';
import { semhColour, semhLabel } from '../../tutor-reports/tutor-report-flows/tutor-report-leap-v2/models/leap-v2-semh';
import { QaApproveItem, QaApproveResult } from '../../_models/qa/qa-approve.model';
import { SubmissionHistoryDialog } from '../../shared/submission-history-dialog';
import { LeapV2SessionService } from '../../tutor-reports/tutor-report-flows/tutor-report-leap-v2/services/leap-v2-session.service';
import { ABSENT_STATUSES } from '../../tutor-reports/tutor-report-flows/tutor-report-leap-v2/models/leap-v2-attendance.model';
import { FeatureSwitchService } from '../../_services/feature-switch/feature-switch.service';
import { FeatureSwitchKeys } from '../../_constants/feature-switch-keys';
import { FilterSpec, FilterState } from '../../shared/filter-ux/filter-state';
import { ScreenFilterUx } from '../../shared/filter-ux/screen-filter-ux';

/**
 * QA report approval (SDD/specs/QAMassApprove.md, work item 33341).
 *
 * Replaces the RDB-connected spreadsheet FES QA used to bulk-move report status
 * Submitted -> Approved. Only Approved reports reach client-facing reports.
 *
 * Two behaviours worth knowing before changing anything here:
 *  - The consultant filter list is *derived* from the reports currently at Submitted, so it is
 *    legitimately empty when there is nothing to QA.
 *  - Approve posts the snapshot the user saw and left checked, never a re-query, so reports
 *    submitted after Run are deliberately excluded.
 */
@Component({
  selector: 'app-qa-report-approval',
  templateUrl: './qa-report-approval.component.html',
  styleUrls: ['./qa-report-approval.component.css'],
  standalone: false
})
export class QaReportApprovalComponent implements OnInit {

  /** Brand is locked to FES for now; the spec keeps it visible but read-only. */
  public readonly brandLabel: string = 'FES';

  /**
   * The labels are display-only; `value` is the wire contract and must stay exactly
   * 'Session' | 'ILP' | 'Progress' (see QaReportType). Typing the literal is what keeps that
   * honest — a bare value="Session" attribute in the template is not type-checked.
   */
  public readonly reportTypeOptions: ReadonlyArray<{ value: QaReportType; label: string }> = [
    { value: 'Session', label: 'Session reports' },
    { value: 'ILP', label: 'ILP reports' },
    { value: 'Progress', label: 'Progress reports' }
  ];

  public consultants: Array<QaConsultant> = new Array<QaConsultant>();
  public selectedConsultantIds: Array<number> = new Array<number>();

  /** Exactly one type is always selected; the search posts it as a one-element array. */
  public selectedReportType: QaReportType = 'Session';

  public rows: Array<QaReportRow> = new Array<QaReportRow>();
  public columns: ColumnDef<QaReportRow>[] = [];
  public emptyMessage: string = 'No submitted reports match your filters.';

  public consultantsLoaded: boolean = false;
  public searchRun: boolean = false;
  public loading: boolean = false;
  public approving: boolean = false;
  public selectAll: boolean = true;

  /**
   * Paging for the review tables. The report-type filter is single-select, so `rows` only ever holds
   * one type and one set of paging state serves whichever table is on screen.
   *
   * Only the *rendering* is paged: `checkedKeys`, `checkedCount` and "Approve all" all read the full
   * `rows`, so the checked snapshot still covers the whole matched set rather than the visible page
   * (spec O5) — which is exactly why the snapshot is keyed rather than held on the row objects.
   */
  public page: number = 1;
  public pageSize: number = QaReportApprovalComponent.DEFAULT_PAGE_SIZE;
  public readonly pageSizeOptions: number[] = DEFAULT_PAGE_SIZE_OPTIONS;

  /** Key the remembered page size is stored under, shared with every other table's convention. */
  private static readonly TABLE_ID: string = 'qa-report-approval';
  private static readonly DEFAULT_PAGE_SIZE: number = 10;

  /**
   * The checked snapshot, keyed by report type *and* id: ids are only unique within a type,
   * so a bare id set would collide across types. Keying this way also means paging and
   * sorting cannot lose the state of rows that are off-screen.
   */
  private checkedKeys: Set<string> = new Set<string>();

  /**
   * The filters.qa-report-approval switch. On, the consultant and report type filters are remembered and
   * carried in the URL, so coming back from a report puts the user back on the same search. Off, the
   * screen is exactly as it was.
   */
  public readonly filtersKey = FeatureSwitchKeys.filtersQaReportApproval;
  // How each session was delivered shows only while the per-session delivery setting is on (39883).
  public deliverySettingOn = false;
  public filtersEnabled: boolean = false;

  /** Query-string names for the filters, which are also what is remembered. Ids and the type only. */
  private static readonly filterSpec: FilterSpec = { consultant: 'number[]', type: 'string' };
  private filterUx: ScreenFilterUx;

  /**
   * Filters to restore, held until the consultant list has loaded: restored ids are checked against it.
   * Null once applied, or when there is nothing to restore.
   */
  private pendingFilterState: FilterState | null = null;

  /** The single placeholder band a learner-less session falls back to (see learnersFor). */
  private static readonly NO_LEARNERS: QaSessionLearner = {
    studentId: 0,
    name: 'No learners recorded',
    keyStage: null,
    attendanceStatus: null,
    attendanceReason: null,
    attendanceComments: null,
    objectiveComments: null,
    engagementComments: null
  };

  constructor(
    private router: Router,
    private featureHelperService: FeatureHelperService,
    public dialog: MatDialog,
    private qaService: QaService,
    private errorModalHelper: ErrorModalHelperService,
    private successModalHelper: SuccessModalHelperService,
    private leapV2SessionService: LeapV2SessionService,
    private leapV2IlpService: LeapV2IlpService,
    private leapV2ProgressReportService: LeapV2ProgressReportService,
    private featureSwitches: FeatureSwitchService,
    @Optional() private route: ActivatedRoute = null
  ) { }

  /** The five learner attributes, in the order the ILP screen captures them. */
  public readonly ilpAttributes: ReadonlyArray<{ key: string; label: string }> = [
    { key: 'confidenceRating', label: 'Confidence' },
    { key: 'resilienceRating', label: 'Resilience' },
    { key: 'independenceRating', label: 'Independence' },
    { key: 'motivationRating', label: 'Motivation' },
    { key: 'selfRegulationRating', label: 'Self-regulation' }
  ];

  ngOnInit() {
    let featureList = new Array<string>();
    featureList.push('QaReportApprovalView');

    let currentUserFeatures = this.featureHelperService.checkFeaturePermissions(featureList);
    if (!currentUserFeatures.filter(f => f.feature === 'QaReportApprovalView')[0].enabled) {
      this.router.navigate(['/login'], { queryParams: { returnUrl: window.location.pathname } });
      return;
    }

    // Restore the size this user last chose, the same way every other table does.
    const remembered = readRememberedPageSize(QaReportApprovalComponent.TABLE_ID);
    if (remembered) {
      this.pageSize = remembered;
    }

    this.buildColumns();
    this.loadConsultants();

    this.filterUx = new ScreenFilterUx(this.filtersKey, QaReportApprovalComponent.filterSpec, this.router, this.route);
    this.featureSwitches.isEnabled(this.filtersKey).subscribe(enabled => {
      this.filtersEnabled = enabled;
      // A shared link's filters, else this user's last ones. Read only once the switch is known to be on.
      this.pendingFilterState = enabled ? this.filterUx.initialState() : null;
      this.restoreFilters();
    });
    this.featureSwitches.isEnabled(FeatureSwitchKeys.sessionDeliverySetting).subscribe(on => this.deliverySettingOn = on);
  }

  /** True once at least one consultant is selected; a report type always is (server re-validates). */
  public get canRun(): boolean {
    return this.selectedConsultantIds.length > 0;
  }

  public get checkedCount(): number {
    return this.checkedKeys.size;
  }

  public get canApproveAll(): boolean {
    return this.searchRun && this.rows.length > 0 && this.checkedCount > 0 && !this.approving;
  }

  /** Total rows the search matched — what the pager counts, not what is on screen. */
  public get total(): number {
    return this.rows.length;
  }

  /** The slice the tables render. Each row carries exactly one kind of detail, so slicing first and
   *  splitting by type after is safe and keeps one page state across all three tables. */
  private get pagedRows(): QaReportRow[] {
    const startIndex = (this.page - 1) * this.pageSize;
    return this.rows.slice(startIndex, startIndex + this.pageSize);
  }

  /**
   * Each report type renders in its own purpose-built table. The filter is single-select, so in
   * practice exactly one of these is ever populated.
   */
  public get sessionRows(): QaReportRow[] {
    return this.pagedRows.filter(r => r.session != null);
  }

  public get ilpRows(): QaReportRow[] {
    return this.pagedRows.filter(r => r.ilp != null);
  }

  public get progressRows(): QaReportRow[] {
    return this.pagedRows.filter(r => r.progress != null);
  }

  /** Whatever carries no review detail of its own — nothing, now all three types have a table. */
  public get flatRows(): QaReportRow[] {
    return this.pagedRows.filter(r => r.session == null && r.ilp == null && r.progress == null);
  }

  public onPageChange(page: number): void {
    this.page = page;
  }

  /**
   * Always returns to page 1 — staying on, say, page 7 after switching 10 to 100 would land the user
   * past the end of the data. Same rule (and same storage key convention) as the shared data table.
   */
  public onPageSizeChange(pageSize: number): void {
    if (!pageSize || pageSize === this.pageSize) {
      return;
    }
    this.pageSize = pageSize;
    this.page = 1;
    rememberPageSize(QaReportApprovalComponent.TABLE_ID, pageSize);
  }

  /** Keeps the page in range after rows leave the grid, so an approve never strands an empty page. */
  private clampPage(): void {
    const lastPage = Math.max(1, Math.ceil(this.rows.length / this.pageSize));
    if (this.page > lastPage) {
      this.page = lastPage;
    }
  }

  /** Signing a single report off, one at a time — the same features add-session-v2 gates Accept/Reject on. */
  public get canApprove(): boolean {
    return this.featureHelperService.hasFeature('TutorReportsApprove');
  }

  public get canReject(): boolean {
    return this.featureHelperService.hasFeature('TutorReportsReject');
  }

  /** The ILP has its own approve/reject features — deliberately not the session report's. */
  public get canApproveIlp(): boolean {
    return this.featureHelperService.hasFeature('IndividualLearningReportsApprove');
  }

  public get canRejectIlp(): boolean {
    return this.featureHelperService.hasFeature('IndividualLearningReportsReject');
  }

  /** The progress report has its own features again — not the session report's or the ILP's. */
  public get canApproveProgress(): boolean {
    return this.featureHelperService.hasFeature('ProgressReportsApprove');
  }

  public get canRejectProgress(): boolean {
    return this.featureHelperService.hasFeature('ProgressReportsReject');
  }

  /** Shown when there is simply nothing awaiting QA — an empty list here is not an error. */
  public get noConsultantsAvailable(): boolean {
    return this.consultantsLoaded && this.consultants.length === 0;
  }

  public run(): void {
    if (!this.canRun) {
      return;
    }

    if (this.filtersEnabled) {
      this.filterUx.save(this.filterState());
    }

    this.loading = true;
    this.qaService.searchReports(this.selectedConsultantIds, [this.selectedReportType]).subscribe({
      next: (response: QaReportRow[]) => {
        this.rows = response == null ? new Array<QaReportRow>() : response;
        // Every row starts checked (spec 3.2); QA deselect the exceptions.
        this.checkedKeys = new Set<string>(this.rows.map(r => this.rowKey(r)));
        this.selectAll = this.rows.length > 0;
        this.searchRun = true;
        this.page = 1; // a new result set always starts at the first page
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.errorModalHelper.show(error.message, error);
      }
    });
  }

  public checked(row: QaReportRow): boolean {
    return row ? this.checkedKeys.has(this.rowKey(row)) : false;
  }

  public assignCheckbox(row: QaReportRow, event): void {
    if (!row) {
      return;
    }
    if (event.checked) {
      this.checkedKeys.add(this.rowKey(row));
    } else {
      this.checkedKeys.delete(this.rowKey(row));
    }
    this.selectAll = this.rows.length > 0 && this.checkedCount === this.rows.length;
  }

  public onAllSelected(event): void {
    // Covers the whole matched set, not just the visible page (spec O5) — the grid pages
    // client-side over the full result, so rows is every match.
    // Reads the event rather than this.selectAll so it does not depend on ngModel having
    // written back before this (change) handler runs.
    this.selectAll = event.checked;
    if (event.checked) {
      this.checkedKeys = new Set<string>(this.rows.map(r => this.rowKey(r)));
    } else {
      this.checkedKeys.clear();
    }
  }

  /**
   * Never empty. A session row's cells span its learners, so a session that somehow arrives with none
   * still has to render one band — otherwise the row emits no <tr> at all and vanishes from the grid,
   * taking its Approve/Reject buttons with it.
   */
  public learnersFor(row: QaReportRow): QaSessionLearner[] {
    const learners = row.session ? row.session.learners : null;
    return learners && learners.length > 0 ? learners : [QaReportApprovalComponent.NO_LEARNERS];
  }

  public learnerCount(row: QaReportRow): number {
    return this.learnersFor(row).length;
  }

  /** Absent and both Cancelled statuses mean there is no report to read (same rule as add-session-v2). */
  public learnerAttended(learner: QaSessionLearner): boolean {
    return (ABSENT_STATUSES as string[]).indexOf(learner.attendanceStatus) === -1;
  }

  public attendanceVariant(learner: QaSessionLearner): StatusVariant {
    if (learner.attendanceStatus === 'Present') {
      return 'green';
    }
    if (learner.attendanceStatus === 'Late') {
      return 'amber';
    }
    return 'red';
  }

  /** Opens the full report the QA row summarises, on the tutor's own screen. */
  public openReport(row: QaReportRow): void {
    this.router.navigate(['/add-session-v2', row.session.placementId, row.session.orbitSessionId]);
  }

  /** Same sign-off as add-session-v2's Accept: confirm, then the LEAP-only Authorise. */
  public approveSession(row: QaReportRow): void {
    this.dialog.open(DynamicConfirmModalComponent, {
      width: '440px',
      data: {
        confirmMessage: 'Accept this session report? Payment was already authorised when the tutor'
          + ' submitted it; this records your sign-off.'
      }
    })
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (!confirmed) {
          return;
        }
        this.leapV2SessionService.authoriseSession(row.session.placementId, row.session.sessionId).subscribe({
          next: () => this.removeRow(row),
          error: (error) => this.errorModalHelper.show(error.message, error)
        });
      });
  }

  /** Same rejection as add-session-v2: a 20-200 character reason, emailed to the tutor only. */
  public rejectSession(row: QaReportRow): void {
    const dialogRef = this.dialog.open(SubmissionHistoryDialog, {
      panelClass: 'my-dialog',
      data: { title: 'Reject session report', label: 'Reason for rejection (emailed to the tutor)' }
    });
    // The dialog's Cancel and close-X both emit `true` from afterClosed(), so only onConfirmed counts.
    const confirmed = dialogRef.componentInstance.onConfirmed.subscribe((reason: string) => {
      dialogRef.close();
      this.leapV2SessionService.rejectSession(row.session.placementId, row.session.sessionId, reason).subscribe({
        next: () => this.removeRow(row),
        error: (error) => this.errorModalHelper.show(error.message, error)
      });
    });
    dialogRef.afterClosed().subscribe(() => confirmed.unsubscribe());
  }

  // ── ILP rows ────────────────────────────────────────────────────────────────────────────────

  /** Movement from the initial to the target assessment score; null when either is unset. */
  public scoreDelta(row: QaReportRow): number | null {
    const initial = row.ilp.initialScore;
    const target = row.ilp.targetScore;
    return initial == null || target == null ? null : target - initial;
  }

  public attributeValue(row: QaReportRow, key: string): number | null {
    return row.ilp[key];
  }

  /** Ratings are 1..5; the label and colour come from the one shared scale definition. */
  public attributeLabel(value: number | null): string {
    return semhLabel(value);
  }

  public attributeColour(value: number | null): string {
    return semhColour(value);
  }

  public openIlpReport(row: QaReportRow): void {
    this.router.navigate(['/individual-learning-plan', row.ilp.placementId, row.ilp.studentId]);
  }

  public approveIlp(row: QaReportRow): void {
    this.dialog.open(DynamicConfirmModalComponent, {
      width: '440px',
      data: { confirmMessage: 'Approve this individual learning plan? It becomes visible to the client.' }
    })
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (!confirmed) {
          return;
        }
        this.leapV2IlpService.authoriseIlp(row.ilp.rowId).subscribe({
          next: () => this.removeRow(row),
          error: (error) => this.errorModalHelper.show(error.message, error)
        });
      });
  }

  public rejectIlp(row: QaReportRow): void {
    const dialogRef = this.dialog.open(SubmissionHistoryDialog, {
      panelClass: 'my-dialog',
      data: { title: 'Reject learning plan', label: 'Reason for rejection (emailed to the tutor when rejection emails are switched on)' }
    });
    // The dialog's Cancel and close-X both emit `true` from afterClosed(), so only onConfirmed counts.
    const confirmed = dialogRef.componentInstance.onConfirmed.subscribe((reason: string) => {
      dialogRef.close();
      this.leapV2IlpService.rejectIlp(row.ilp.rowId, reason).subscribe({
        next: () => this.removeRow(row),
        error: (error) => this.errorModalHelper.show(error.message, error)
      });
    });
    dialogRef.afterClosed().subscribe(() => confirmed.unsubscribe());
  }

  // ── Progress report rows ────────────────────────────────────────────────────────────────────

  /**
   * The five attributes as previous/current pairs, in the order the report captures them. Returning
   * the pair rather than a key keeps the template free of index gymnastics.
   */
  public progressAttributes(row: QaReportRow): Array<{ label: string; previous: number | null; current: number | null }> {
    const progress = row.progress;
    return [
      { label: 'Confidence', previous: progress.previousConfidenceRating, current: progress.currentConfidenceRating },
      { label: 'Resilience', previous: progress.previousResilienceRating, current: progress.currentResilienceRating },
      { label: 'Independence', previous: progress.previousIndependenceRating, current: progress.currentIndependenceRating },
      { label: 'Motivation', previous: progress.previousMotivationRating, current: progress.currentMotivationRating },
      { label: 'Self-regulation', previous: progress.previousSelfRegulationRating, current: progress.currentSelfRegulationRating }
    ];
  }

  /** Says where the previous values came from, so the reviewer knows what they are comparing against. */
  public progressBaselineCaption(row: QaReportRow): string {
    return row.progress.previousFromIlp ? 'Measured from ILP baseline' : 'Measured from previous report';
  }

  public openProgressReport(row: QaReportRow): void {
    this.router.navigate(['/progress-report', row.progress.placementId, row.progress.studentId],
      { queryParams: { reportId: row.progress.reportId } });
  }

  public approveProgress(row: QaReportRow): void {
    this.dialog.open(DynamicConfirmModalComponent, {
      width: '440px',
      data: { confirmMessage: 'Approve this progress report? It becomes visible to the client.' }
    })
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (!confirmed) {
          return;
        }
        this.leapV2ProgressReportService.authoriseProgressReport(row.progress.reportId).subscribe({
          next: () => this.removeRow(row),
          error: (error) => this.errorModalHelper.show(error.message, error)
        });
      });
  }

  public rejectProgress(row: QaReportRow): void {
    const dialogRef = this.dialog.open(SubmissionHistoryDialog, {
      panelClass: 'my-dialog',
      data: { title: 'Reject progress report', label: 'Reason for rejection (emailed to the tutor when rejection emails are switched on)' }
    });
    // The dialog's Cancel and close-X both emit `true` from afterClosed(), so only onConfirmed counts.
    const confirmed = dialogRef.componentInstance.onConfirmed.subscribe((reason: string) => {
      dialogRef.close();
      this.leapV2ProgressReportService.rejectProgressReport(row.progress.reportId, reason).subscribe({
        next: () => this.removeRow(row),
        error: (error) => this.errorModalHelper.show(error.message, error)
      });
    });
    dialogRef.afterClosed().subscribe(() => confirmed.unsubscribe());
  }

  /**
   * The grid lists reports *awaiting* QA, so one that has just been approved or rejected no longer
   * belongs in it — unlike add-session-v2, which stays on the report and flips its status badge.
   */
  private removeRow(row: QaReportRow): void {
    this.checkedKeys.delete(this.rowKey(row));
    this.rows = this.rows.filter(r => r !== row);
    this.selectAll = this.rows.length > 0 && this.checkedCount === this.rows.length;
    // Actioning the last row on the last page would otherwise leave the user on an empty one.
    this.clampPage();
  }

  public approveAll(): void {
    if (!this.canApproveAll) {
      return;
    }

    const items: QaApproveItem[] = this.rows
      .filter(r => this.checked(r))
      .map(r => ({ reportType: r.reportType, id: r.id }));

    const plural = items.length === 1 ? 'report' : 'reports';
    this.dialog.open(DynamicConfirmModalComponent, {
      width: '440px',
      data: { confirmMessage: 'Approve ' + items.length + ' ' + plural + '?' }
    })
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (!confirmed) {
          return;
        }
        this.approve(items);
      });
  }

  private approve(items: QaApproveItem[]): void {
    this.approving = true;
    this.qaService.approveReports(items).subscribe({
      next: (result: QaApproveResult) => {
        this.approving = false;
        this.successModalHelper.show(this.approveMessage(result));
        this.reset();
      },
      error: (error) => {
        this.approving = false;
        this.errorModalHelper.show(error.message, error);
      }
    });
  }

  /**
   * Skipped rows are reported, not swallowed — a non-zero count means someone else moved
   * those reports off Submitted between the search and the approve.
   */
  private approveMessage(result: QaApproveResult): string {
    const approved = result == null ? 0 : result.approvedCount;
    const skipped = result == null ? 0 : result.skippedCount;
    let message = approved + (approved === 1 ? ' report approved.' : ' reports approved.');
    if (skipped > 0) {
      message += ' ' + skipped + (skipped === 1 ? ' report was skipped' : ' reports were skipped')
        + ' because it was no longer awaiting approval.';
    }
    return message;
  }

  /** Clears the grid and the filter selections — the Reset button, and after a successful approve (spec 3.3 step 5). */
  public reset(): void {
    this.rows = new Array<QaReportRow>();
    this.checkedKeys.clear();
    this.selectedConsultantIds = new Array<number>();
    this.selectedReportType = 'Session';
    this.searchRun = false;
    this.selectAll = true;
    this.page = 1;
    if (this.filtersEnabled) {
      this.filterUx.clear();
    }
    // The consultant list is derived from submitted reports, so both approving and a plain
    // Reset can leave it stale — reload rather than show a list that no longer matches.
    this.loadConsultants();
  }

  private loadConsultants(): void {
    this.consultantsLoaded = false;
    this.qaService.getConsultants().subscribe({
      next: (response: QaConsultant[]) => {
        this.consultants = response == null ? new Array<QaConsultant>() : response;
        this.consultantsLoaded = true;
        this.restoreFilters();
      },
      error: (error) => {
        this.consultantsLoaded = true;
        this.errorModalHelper.show(error.message, error);
      }
    });
  }

  private filterState(): FilterState {
    return { consultant: this.selectedConsultantIds, type: this.selectedReportType };
  }

  /**
   * Applies the remembered or linked filters once both the switch and the consultant list are in, then
   * runs the search. The consultant list only holds consultants with reports still awaiting QA, so a
   * restored id that is no longer in it is dropped. Nothing runs when none of the consultants are left.
   */
  private restoreFilters(): void {
    if (!this.pendingFilterState || !this.consultantsLoaded) {
      return;
    }
    const state = this.pendingFilterState;
    this.pendingFilterState = null;

    const restoredIds = (state.consultant as number[]) ?? [];
    this.selectedConsultantIds = restoredIds.filter(id => this.consultants.some(c => c.id === id));
    const type = this.reportTypeOptions.find(o => o.value === state.type);
    if (type) {
      this.selectedReportType = type.value;
    }

    if (this.canRun) {
      this.run();
    }
  }

  private rowKey(row: QaReportRow): string {
    return row.reportType + ':' + row.id;
  }

  private buildColumns(): void {
    this.columns = [
      { field: 'select', header: '', type: 'template', templateName: 'select', width: '60px' },
      { field: 'reportType', header: 'Type', type: 'text', sortable: true },
      { field: 'createdDate', header: 'Created', type: 'date', dateFormat: 'dd MMM yyyy', sortable: true },
      { field: 'educatorName', header: 'Educator', type: 'text', sortable: true },
      { field: 'studentName', header: 'Student', type: 'text', sortable: true },
      { field: 'consultantName', header: 'Tuition Consultant', type: 'text', sortable: true }
    ];
  }
}
